// v2 scorpion — fully articulated: three overlapping armour plates down the back
// with a head shield and eye dots, eight two-segment legs arched up at the knee,
// two jointed pincer arms (arm, forearm turning inward, broad hand, open
// thumb-and-finger pinch) — and the signature tail: five tapering ball segments
// arcing up and right over the back into a venom-red telson bulb whose sting
// curls forward-down, poised to strike. Two liveries: mahogany-amber and a
// near-black slate. Ported 1:1 from age-of-mcp's v2/scorpion.ts, using the
// library's shared `wildland` (tube) helper.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { body: "#4a2c14", plate: "#5e3a1c", limb: "#3e2510", pincer: "#c98a3a", sting: "#8a3018" },
  { body: "#26201a", plate: "#36302a", limb: "#1e1a14", pincer: "#5a5248", sting: "#7a2a1a" },
];

function scorpionParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // three armour plates down the back + head shield
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.body,
      position: [0.012, 0.011, 0], scale: [1.2, 0.55, 1.1] },
    { geometry: new THREE.IcosahedronGeometry(0.0135, 0), color: c.plate,
      position: [-0.004, 0.0115, 0], scale: [1.15, 0.6, 1.15] },
    { geometry: new THREE.IcosahedronGeometry(0.0115, 0), color: c.body,
      position: [-0.02, 0.011, 0], scale: [1.1, 0.55, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.plate,
      position: [0.024, 0.0115, 0], scale: [1.1, 0.5, 0.95] },
  ];
  for (const s of [1, -1] as const) {
    // eye dots on the head shield
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.0018, 0),
      color: "#0e0a06", position: [0.028, 0.017, s * 0.004] });
    // four two-segment legs, knees arched above the body line
    for (const hipX of [0.016, 0.006, -0.004, -0.014]) {
      parts.push(
        wildland.tube([hipX, 0.009, s * 0.012], [hipX - 0.001, 0.014, s * 0.022],
          0.0016, 0.0013, c.limb),
        wildland.tube([hipX - 0.001, 0.014, s * 0.022], [hipX - 0.004, 0, s * 0.029],
          0.0012, 0.0007, c.limb),
      );
    }
    // jointed pincer arm: arm out, forearm turning inward, hand, open pinch
    parts.push(
      wildland.tube([0.024, 0.01, s * 0.01], [0.04, 0.011, s * 0.02],
        0.0028, 0.0035, c.limb),
      wildland.tube([0.04, 0.011, s * 0.02], [0.052, 0.011, s * 0.016],
        0.0035, 0.004, c.body),
      { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: c.pincer,
        position: [0.058, 0.012, s * 0.014], scale: [1.45, 0.8, 1.0] },
      { geometry: new THREE.ConeGeometry(0.0028, 0.012, 3), color: c.pincer,
        position: [0.069, 0.0145, s * 0.0125], rotation: [0, 0, -1.75] },
      { geometry: new THREE.ConeGeometry(0.0028, 0.012, 3), color: c.pincer,
        position: [0.069, 0.0095, s * 0.0135], rotation: [0, 0, -1.35] },
    );
  }
  // the tail — five tapering segments arcing up and over the back
  const arc: Array<[number, number, number]> = [
    [-0.032, 0.018, 0.0052], [-0.041, 0.03, 0.0049], [-0.043, 0.043, 0.0046],
    [-0.037, 0.055, 0.0043], [-0.026, 0.063, 0.004],
  ];
  for (let i = 0; i < arc.length; i++) {
    const seg = arc[i]!;
    parts.push({ geometry: new THREE.IcosahedronGeometry(seg[2], 0),
      color: i % 2 === 0 ? c.body : c.plate, position: [seg[0], seg[1], 0] });
  }
  // venom telson bulb + sting curling forward-down over the head
  parts.push(
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: c.sting,
      position: [-0.018, 0.067, 0] },
    { geometry: new THREE.ConeGeometry(0.0035, 0.013, 4), color: c.sting,
      position: [-0.0115, 0.0665, 0], rotation: [0, 0, -2.1] },
  );
  return parts;
}

export const scorpionV2 = defineModel({
  id: "scorpion-v2",
  batchId: "fable",
  name: "Scorpion (v2)",
  tags: ["creature","scorpion","low-poly","age-of-mcp","fable"],
  summary: "Low-poly scorpion.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => scorpionParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => scorpionParts(1) },
  ],
  animations: [],
});
